import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  SupplierInstallationCreatePayload,
  SupplierInstallationDto,
  SupplierInstallationListItemDto,
  SupplierInstallationUpdatePayload,
  OrganizationSupplierInstallationListItemDto,
} from '@/features/suppliers/lib/supplier-installation-types'
import { apiClient } from '@/shared/api/apiClient'
import { useSession } from '@/shared/hooks/useSession'

export type {
  SupplierInstallationCreatePayload,
  SupplierInstallationDto,
  SupplierInstallationListItemDto,
  SupplierInstallationUpdatePayload,
  OrganizationSupplierInstallationListItemDto,
}

export const organizationSupplierInstallationsQueryKey = (
  organizationId: string | undefined
) => ['organization-supplier-installations', organizationId] as const

export const supplierInstallationsQueryKey = (
  organizationId: string | undefined,
  supplierOrganizationId: string | undefined
) => ['supplier-installations', organizationId, supplierOrganizationId] as const

export const supplierInstallationQueryKey = (
  organizationId: string | undefined,
  supplierOrganizationId: string | undefined,
  supplierInstallationId: string | undefined
) =>
  [
    'supplier-installation',
    organizationId,
    supplierOrganizationId,
    supplierInstallationId,
  ] as const

/** Lists all supplier installations in the current session organization (org-wide). */
export const useOrganizationSupplierInstallationsQuery = () => {
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  return useQuery({
    queryKey: organizationSupplierInstallationsQueryKey(organizationId),
    queryFn: async () => {
      const response = await apiClient.get<{
        supplierInstallations: OrganizationSupplierInstallationListItemDto[]
      }>(`/v2/organizations/${organizationId}/supplier-installations`)
      return response.data.supplierInstallations
    },
    enabled: Boolean(organizationId),
  })
}

/** Lists supplier installations for a supplier organization. */
export const useSupplierInstallationsQuery = (
  supplierOrganizationId: string | undefined
) => {
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  return useQuery({
    queryKey: supplierInstallationsQueryKey(organizationId, supplierOrganizationId),
    queryFn: async () => {
      const response = await apiClient.get<SupplierInstallationListItemDto[]>(
        `/v2/organizations/${organizationId}/supplier-organizations/${supplierOrganizationId}/supplier-installations`
      )
      return response.data
    },
    enabled: Boolean(organizationId && supplierOrganizationId),
  })
}

/** Fetches a single supplier installation by id for a supplier organization. */
export const useSupplierInstallationQuery = (
  supplierOrganizationId: string | undefined,
  supplierInstallationId: string | undefined
) => {
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  return useQuery({
    queryKey: supplierInstallationQueryKey(
      organizationId,
      supplierOrganizationId,
      supplierInstallationId
    ),
    queryFn: async () => {
      const response = await apiClient.get<SupplierInstallationDto>(
        `/v2/organizations/${organizationId}/supplier-organizations/${supplierOrganizationId}/supplier-installations/${supplierInstallationId}`
      )
      return response.data
    },
    enabled: Boolean(organizationId && supplierOrganizationId && supplierInstallationId),
  })
}

/** Creates, updates, and deletes supplier installations for a supplier organization. */
export const useSupplierInstallationMutations = (
  supplierOrganizationId: string | undefined
) => {
  const queryClient = useQueryClient()
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  const invalidateList = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: supplierInstallationsQueryKey(organizationId, supplierOrganizationId),
      }),
      queryClient.invalidateQueries({
        queryKey: organizationSupplierInstallationsQueryKey(organizationId),
      }),
    ])

  const addSupplierInstallation = useMutation({
    mutationFn: async (payload: SupplierInstallationCreatePayload) => {
      const response = await apiClient.post<SupplierInstallationDto>(
        `/v2/organizations/${organizationId}/supplier-organizations/${supplierOrganizationId}/supplier-installations`,
        payload
      )
      return response.data
    },
    onSuccess: invalidateList,
  })

  const updateSupplierInstallation = useMutation({
    mutationFn: async ({
      supplierInstallationId,
      payload,
    }: {
      supplierInstallationId: string
      payload: SupplierInstallationUpdatePayload
    }) => {
      const response = await apiClient.put<SupplierInstallationDto>(
        `/v2/organizations/${organizationId}/supplier-organizations/${supplierOrganizationId}/supplier-installations/${supplierInstallationId}`,
        payload
      )
      return response.data
    },
    onSuccess: async (_data, variables) => {
      await invalidateList()
      await queryClient.invalidateQueries({
        queryKey: supplierInstallationQueryKey(
          organizationId,
          supplierOrganizationId,
          variables.supplierInstallationId
        ),
      })
    },
  })

  const deleteSupplierInstallation = useMutation({
    mutationFn: async (supplierInstallationId: string) => {
      await apiClient.delete(
        `/v2/organizations/${organizationId}/supplier-organizations/${supplierOrganizationId}/supplier-installations/${supplierInstallationId}`
      )
    },
    onSuccess: invalidateList,
  })

  return {
    addSupplierInstallation,
    updateSupplierInstallation,
    deleteSupplierInstallation,
  }
}
